import { Map } from "immutable";

// @ts-ignore
import { updateSpacingValue } from "@seneca/spacing";

import objectToJS from "seneca-common/utils/functions/immutable/props-to-js";
import logError, { logMessage } from "seneca-common/utils/sentry/logError";

import actionTypes from "../../../../action-types";
import InvalidModuleScoreError from "../../../../errors/InvalidModuleScoreError";
import ModuleStudiedEvent from "../../../../models/ModuleStudiedEvent";
import {
  ContentStat,
  ContentStats
} from "../../../../models/state/contentStats";
import { checkIsInvalidScore } from "../../../../utils/checkIsInvalidScore";
import transformServerTimestamp from "../../../../utils/transformServerTimestamp";
import { updateAverageScore } from "../../../../utils/updateAverageScore";

export const contentStats = (
  contentStats: ContentStats = Map<string, ContentStat>(),
  action: Record<string, any>
): ContentStats => {
  switch (action.type) {
    case actionTypes.COMPLETE_MODULE: {
      return contentStats.update(
        action.payload.moduleStudiedEvent.contentId,
        meta => individualContentStat(meta, action)
      );
    }

    case actionTypes.RECEIVE_CONTENT_STUDY_STATS: {
      const newStats: ContentStats = action.payload.contentStudyStats.reduce(
        (newStats: any, stat: any) => {
          return newStats.set(stat.contentId, transformServerContentStat(stat));
        },
        Map()
      );
      return contentStats.merge(newStats);
    }

    case actionTypes.RECEIVE_AND_RESET_CONTENT_STUDY_STATS: {
      action.payload.contentIds.forEach((contentId: string) => {
        contentStats = contentStats.delete(contentId);
      });
      const newStats = action.payload.contentStudyStats.reduce(
        (newStats: any, stat: any) => {
          return newStats.set(stat.contentId, transformServerContentStat(stat));
        },
        Map()
      );
      return contentStats.merge(newStats);
    }

    case actionTypes.RECEIVE_RECONCILED_CONTENT_STUDY_STATS: {
      const newStats = action.payload.contentStudyStats.reduce(
        (newStats: any, stat: any) => {
          return newStats.set(stat.contentId, new ContentStat(stat));
        },
        Map()
      );
      return contentStats.merge(newStats);
    }

    default:
      return contentStats;
  }
};

export const individualContentStat = (
  contentStat: ContentStat = new ContentStat(),
  action: Record<string, any>
) => {
  switch (action.type) {
    case actionTypes.COMPLETE_MODULE: {
      return applyCompleteModuleEventToContentStat(
        contentStat,
        action.payload.moduleStudiedEvent
      );
    }

    default:
      return contentStat;
  }
};

export const applyCompleteModuleEventToContentStat = (
  contentStat: ContentStat,
  {
    sessionId,
    courseId,
    sectionId,
    contentId,
    moduleId,
    timeFinished,
    testingActive,
    gaveUp,
    score
  }: ModuleStudiedEvent,
  {
    logUpdateSpacingWarnings = true
  }: {
    logUpdateSpacingWarnings?: boolean;
  } = {}
) => {
  const { timesTested, lastStudied } = contentStat;
  const newTimesTested = testingActive ? timesTested + 1 : timesTested;

  const scoreInvalid = checkIsInvalidScore(score, testingActive);
  const debugInfo = {
    sessionId,
    courseId,
    sectionId,
    contentId,
    moduleId,
    score
  };

  if (scoreInvalid) {
    logError(new InvalidModuleScoreError(score), {
      extraInfo: debugInfo
    });
  }

  let updatedMeta = contentStat.withMutations(contentStat => {
    contentStat
      .set("contentId", contentId!)
      .update("timesStudied", count => count + 1)
      .set("lastContentModuleId", moduleId!)
      .set("lastStudied", timeFinished!)
      .update("spacing", s =>
        updateSpacingValue(
          s,
          lastStudied,
          timeFinished,
          logUpdateSpacingWarnings &&
            function (message: string, meta: Record<string, any>) {
              logMessage(
                message,
                objectToJS({ ...meta, contentStat, ...debugInfo }) as any
              );
            }
        )
      );

    if (testingActive) {
      contentStat
        .set("timesTested", newTimesTested)
        .update("timesCorrect", t => (score === 1 ? t + 1 : t))
        .update("timesIncorrect", t => (score! < 1 ? t + 1 : t))
        .update("timesGivenUp", t => (gaveUp ? t + 1 : t))
        .update("lastScore", s => (!scoreInvalid ? score : s))
        .update("averageScore", currentAverage =>
          !scoreInvalid
            ? updateAverageScore(currentAverage!, newTimesTested, score!)
            : currentAverage
        );
    }
  });
  return updatedMeta;
};

export const transformServerContentStat = (
  serverContentStat: Record<string, any>
) =>
  new ContentStat({
    ...serverContentStat,
    lastStudied: transformServerTimestamp(serverContentStat.lastStudied)
  });
